import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCcw, Download, Play, Eye } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import Loading from '../../components/Loading/Loading';

const AuditReconciliation = () => {
  const [dashboard, setDashboard] = useState(null);
  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState([]);
  const [matched, setMatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const [executing, setExecuting] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setAlert(null);
    try {
      const [dashRes, batchRes, errRes, matchRes] = await Promise.all([
        api.get('/audit/reconciliation/dashboard'),
        api.get('/audit/reconciliation/batches'),
        api.get('/audit/reconciliation/errors', { params: { limit: 20 } }),
        api.get('/audit/reconciliation/matched', { params: { limit: 20 } }),
      ]);
      setDashboard(dashRes.data);
      setBatches(Array.isArray(batchRes.data) ? batchRes.data : []);
      setErrors(Array.isArray(errRes.data) ? errRes.data : []);
      setMatched(Array.isArray(matchRes.data) ? matchRes.data : []);
    } catch (error) {
      setAlert({ type: 'danger', message: error?.response?.data?.message || 'Failed to load reconciliation dashboard.' });
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);


  const handleDryRun = async () => {
    setExecuting(true);
    try {
      const res = await api.post('/audit/reconciliation/dry-run');
      setAlert({ type: 'success', message: `Dry run completed: ${res.data.dryRunInserted || 0} rows inserted.` });
      fetchDashboard();
    } catch (error) {
      setAlert({ type: 'danger', message: error?.response?.data?.error || 'Dry run failed.' });
    } finally {
      setExecuting(false);
    }
  };


  const handleExecute = async () => {
    if (!window.confirm('Execute migration? This will match auditq records with onealpha projects.')) return;
    setExecuting(true);
    try {
      const res = await api.post('/audit/reconciliation/execute');
      setAlert({ type: 'success', message: `Migration completed: ${res.data.inserted || 0} rows inserted.` });
      fetchDashboard();
    } catch (error) {
      setAlert({ type: 'danger', message: error?.response?.data?.error || 'Migration failed.' });
    } finally {
      setExecuting(false);
    }
  };


  const handleExportErrors = async () => {
    try {
      const res = await api.get('/audit/reconciliation/errors/export', { params: { limit: 1000, format: 'csv' }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', 'reconciliation_errors.csv');
      document.body.appendChild(link); link.click(); link.remove();
    } catch (error) {
      setAlert({ type: 'danger', message: 'Export failed.' });
    }
  };

  const status = dashboard?.status || {};
  const nightly = dashboard?.nightlyTotals || {};
  const statCards = [
    { label: 'AuditQ Total', value: status.auditq_total || nightly.auditq_total || 0 },
    { label: 'OneAlpha Total', value: status.onealpha_total || nightly.onealpha_total || 0 },
    { label: 'Matched', value: status.matched_count || nightly.matched_total || 0 },
    { label: 'Errors (24h)', value: nightly.error_total || 0 },
  ];


  return (
    <div className="container-xl py-4">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title mb-1">AuditQ Migration Reconciliation</h2>
            <div className="text-muted">Reconcile AuditQ inventory against onealpha project records.</div>
          </div>
          <div className="col-auto d-flex gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={handleDryRun} disabled={executing}>
              <Eye size={16} className="me-1" /> Dry Run
            </button>
            <button type="button" className="btn btn-danger" onClick={handleExecute} disabled={executing}>
              <Play size={16} className="me-1" /> Execute Migration
            </button>
            <button type="button" className="btn btn-outline-primary" onClick={fetchDashboard} disabled={loading}>
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}


      {loading ? <Loading /> : (
        <>
          <div className="row g-3 mb-3">
            {statCards.map((card) => (
              <div className="col-sm-6 col-xl-3" key={card.label}>
                <div className="border rounded p-3 bg-white h-100">
                  <div className="text-muted small">{card.label}</div>
                  <div className="h2 mb-0">{card.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title mb-0">Batches</h3>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead><tr><th>Batch ID</th><th>Type</th><th>Status</th><th>Matched</th><th>Errors</th><th>Started</th></tr></thead>
                    <tbody>
                      {batches.length === 0 ? <tr><td colSpan={6} className="text-muted text-center">No batches yet</td></tr> :
                        batches.map((b) => (
                          <tr key={b.batch_id}>
                            <td className="text-mono" style={{fontSize:'0.75rem'}}>{b.batch_id}</td>
                            <td>{b.batch_type}</td>
                            <td><span className={`badge bg-${b.status === 'COMPLETED' ? 'green-lt' : b.status === 'FAILED' ? 'red-lt' : 'yellow-lt'}`}>{b.status}</span></td>
                            <td>{b.matched_count}</td>
                            <td>{b.error_count}</td>
                            <td className="text-muted">{b.started_at ? new Date(b.started_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h3 className="card-title mb-0">Unmatched / Errors</h3>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleExportErrors} title="Export errors as CSV">
                    <Download size={14} /> Export CSV
                  </button>
                </div>
                <div className="table-responsive" style={{maxHeight:'300px',overflowY:'auto'}}>
                  <table className="table table-vcenter card-table">
                    <thead><tr><th>Source</th><th>Doc Code</th><th>Client Name</th><th>Status</th></tr></thead>
                    <tbody>
                      {errors.length === 0 ? <tr><td colSpan={4} className="text-muted text-center">No errors</td></tr> :
                        errors.map((e) => (
                          <tr key={e.id}>
                            <td><span className="badge bg-blue-lt">{e.source}</span></td>
                            <td>{e.document_code}</td>
                            <td>{e.client_name}</td>
                            <td><span className={`badge ${e.status === 'ERROR' ? 'bg-red-lt' : 'bg-yellow-lt'}`}>{e.status}</span></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Matched Records</h3>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead><tr><th>Source</th><th>Client Name</th><th>Target ID</th><th>Matched At</th></tr></thead>
                <tbody>
                  {matched.length === 0 ? <tr><td colSpan={4} className="text-muted text-center">No matched records</td></tr> :
                    matched.map((m) => (
                      <tr key={m.id}>
                        <td><span className="badge bg-green-lt">{m.source}</span></td>
                        <td>{m.client_name}</td>
                        <td>{m.target_id || '-'}</td>
                        <td className="text-muted">{m.matched_on ? new Date(m.matched_on).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AuditReconciliation;
